import { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, File, Image, FileText, Trash2, Download, Eye, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'medical_record', label: 'Medical Record' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'insurance_doc', label: 'Insurance Document' },
  { value: 'other', label: 'Other' }
];

const DocumentUpload = ({ patientId, encounterId = null, onUploadComplete }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    document_type: 'other',
    description: '',
    file: null
  });
  const fileInputRef = useRef(null);

  // Fetch documents on mount
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const endpoint = encounterId 
        ? `${API}/documents/encounter/${encounterId}`
        : `${API}/documents/patient/${patientId}`;
      const response = await axios.get(endpoint);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, PNG, and PDF files are allowed.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    // Auto-fill name if empty
    if (!uploadForm.name) {
      setUploadForm(prev => ({ ...prev, name: file.name.split('.')[0], file }));
    } else {
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:xxx;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.name) {
      toast.error('Please select a file and provide a name');
      return;
    }

    setUploading(true);
    try {
      const base64Data = await fileToBase64(uploadForm.file);
      
      await axios.post(`${API}/documents`, {
        name: uploadForm.name,
        document_type: uploadForm.document_type,
        description: uploadForm.description,
        file_data: base64Data,
        file_type: uploadForm.file.type,
        patient_id: patientId,
        encounter_id: encounterId
      });

      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ name: '', document_type: 'other', description: '', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
      if (onUploadComplete) onUploadComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // View document
  const handleView = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}`);
      setViewingDocument(response.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  // Delete document
  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  // Download document
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}`);
      const link = document.createElement('a');
      link.href = `data:${doc.file_type};base64,${response.data.file_data}`;
      link.download = doc.name + (doc.file_type.includes('pdf') ? '.pdf' : '.jpg');
      link.click();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Get icon for file type
  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-slate-500" />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Documents</h3>
        <Button
          size="sm"
          onClick={() => {
            fetchDocuments();
            setShowUploadModal(true);
          }}
          className="gap-2"
        >
          <Upload className="w-4 h-4" />
          Upload
        </Button>
      </div>

      {/* Document List */}
      {loading ? (
        <div className="text-center py-4 text-slate-500">Loading...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
          <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No documents yet</p>
          <p className="text-sm">Upload medical records, lab results, or other files</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
            >
              {getFileIcon(doc.file_type)}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate">{doc.name}</p>
                <p className="text-xs text-slate-500">
                  {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || 'Other'} • {formatFileSize(doc.file_size)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleView(doc)}
                  title="View"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDownload(doc)}
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(doc.id)}
                  title="Delete"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label>Document Name *</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Lab Results Dec 2024"
                required
              />
            </div>

            <div>
              <Label>Document Type</Label>
              <Select
                value={uploadForm.document_type}
                onValueChange={(val) => setUploadForm(prev => ({ ...prev, document_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Input
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
              />
            </div>

            <div>
              <Label>File *</Label>
              <div className="mt-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-slate-500 mt-1">JPG, PNG, or PDF (max 10MB)</p>
              </div>
              {uploadForm.file && (
                <p className="text-sm text-green-600 mt-2">
                  Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={uploading} className="flex-1">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewingDocument && getFileIcon(viewingDocument.file_type)}
              {viewingDocument?.name}
            </DialogTitle>
          </DialogHeader>
          {viewingDocument && (
            <div className="overflow-auto max-h-[70vh]">
              {viewingDocument.file_type?.includes('image') ? (
                <img
                  src={`data:${viewingDocument.file_type};base64,${viewingDocument.file_data}`}
                  alt={viewingDocument.name}
                  className="max-w-full h-auto rounded-lg"
                />
              ) : viewingDocument.file_type?.includes('pdf') ? (
                <iframe
                  src={`data:application/pdf;base64,${viewingDocument.file_data}`}
                  className="w-full h-[60vh] rounded-lg border"
                  title={viewingDocument.name}
                />
              ) : (
                <p className="text-center text-slate-500 py-8">Preview not available</p>
              )}
              {viewingDocument.description && (
                <p className="text-sm text-slate-600 mt-4 p-3 bg-slate-50 rounded-lg">
                  {viewingDocument.description}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentUpload;
